import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import {
  buildProductAttributeTemplatePayload,
  countFilledProductAttributes,
} from '@/temu-platform/utils/temuProductAttributeTemplate';
import { nextTemplatePriority } from '@/temu-platform/utils/temuTemplatePriority';
import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 从商品模板把当前类目属性另存到属性库：仅元信息弹窗（类目只读带回）。
 */
export default function SaveAttributeTemplateMetaModal({
  open,
  shopId,
  officialMode = false,
  shopType = '',
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
  runWithoutUnloadPrompt,
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || !productTypeId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setPriorityLoading(true);
    const loadPriority = async () => {
      try {
        const res = officialMode
          ? await adminAPI.listOfficialTemuAttributeTemplates({
            productTypeId,
            shopType,
            page: 1,
            pageSize: 100,
          })
          : await temuPlatformAPI.listProductAttributeTemplates(shopId, {
            productTypeId,
            page: 1,
            pageSize: 100,
          });
        if (cancelled) return;
        setSortOrder(nextTemplatePriority(res?.items));
      } catch {
        if (!cancelled) setSortOrder(0);
      } finally {
        if (!cancelled) setPriorityLoading(false);
      }
    };
    if (typeof runWithoutUnloadPrompt === 'function') {
      void runWithoutUnloadPrompt(loadPriority);
    } else {
      void loadPriority();
    }
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, officialMode, shopType, runWithoutUnloadPrompt]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('saveTpl.needStyle', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('saveTpl.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('saveTpl.needCategory', fixedUi("缺少适用类目")), 'error');
      return;
    }
    const payload = buildProductAttributeTemplatePayload({
      productAttributes,
      attrDefs,
      productTypeId,
      categoryId,
      categoryPathLabel,
    });
    if (!countFilledProductAttributes(payload.productAttributes)) {
      showToast(chartText('templateEditor.fillAttrsFirst', fixedUi("请先填写类目属性再保存到属性库")), 'error');
      return;
    }

    const persist = async () => {
      setSaving(true);
      try {
        if (officialMode) {
          await adminAPI.createOfficialTemuAttributeTemplate({
            name: title,
            styleNo: styleNoValue,
            remark: String(remark || '').trim() || undefined,
            productTypeId,
            categoryId,
            categoryPathLabel: categoryPathLabel || undefined,
            autoRuleEnabled,
            sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
            shopType,
            payload,
          });
        } else {
          await temuPlatformAPI.createProductAttributeTemplate(shopId, {
            name: title,
            styleNo: styleNoValue,
            remark: String(remark || '').trim() || undefined,
            productTypeId,
            categoryId,
            categoryPathLabel: categoryPathLabel || undefined,
            autoRuleEnabled,
            sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
            payload,
          });
        }
        showToast(chartText('saveTpl.savedAttr', '已保存到属性库'), 'success');
        onSaved?.();
        onClose?.();
      } catch (e) {
        showToast(e?.message || chartText('saveTpl.saveAttrFail', '保存到属性库失败'), 'error');
      } finally {
        setSaving(false);
      }
    };

    if (typeof runWithoutUnloadPrompt === 'function') {
      await runWithoutUnloadPrompt(persist);
    } else {
      await persist();
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-attr-save-meta-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-attr-save-meta-title">{officialMode ? chartText('saveTpl.saveOfficialAttr', fixedUi("保存到官方属性库")) : chartText('saveTpl.saveAttr', fixedUi("保存到属性库"))}</h2>
          <button type="button" className="tpo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="tpo-batch-modal__body">
            <div className="tpo-wizard__form tpo-template-create-form">
              <label className="tpo-field tpo-field--full">
                <span className="tpo-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
                <input
                  className="tpo-input"
                  value={styleNo}
                  disabled={saving}
                  maxLength={128}
                  placeholder={chartText('saveTpl.stylePh', fixedUi("请输入款号"))}
                  onChange={(e) => setStyleNo(e.target.value)}
                />
              </label>
              <label className="tpo-field tpo-field--full">
                <span className="tpo-field__label">{chartText('productTemplate.colName', fixedUi("模板名称"))} <RequiredMark /></span>
                <input
                  className="tpo-input"
                  value={name}
                  disabled={saving}
                  maxLength={128}
                  placeholder={chartText('saveTpl.namePhAttr', fixedUi("如：女装夏季基础属性"))}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="tpo-field tpo-field--full">
                <span className="tpo-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
                <input
                  className="tpo-input"
                  value={remark}
                  disabled={saving}
                  maxLength={200}
                  placeholder={chartText('publishSpec.patternOptional', fixedUi("可选"))}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </label>
              <div className="tpo-field tpo-field--full tpo-template-auto-rule">
                <div className="tpo-template-auto-rule__switch">
                  <TemuSwitch
                    checked={autoRuleEnabled}
                    disabled={saving}
                    ariaLabel={chartText('productTemplate.colAutoRule', '自动规则')}
                    onChange={setAutoRuleEnabled}
                  />
                  <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
                </div>
                <p className="tpo-wizard__inline-hint">
                  {chartText('saveTpl.autoHint', '开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先。')}
                </p>
                <label className="tpo-field">
                  <span className="tpo-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                  <input
                    className="tpo-input"
                    type="number"
                    step={1}
                    value={sortOrder}
                    disabled={saving || priorityLoading}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setSortOrder(Number.isFinite(next) ? next : 0);
                    }}
                  />
                </label>
              </div>
              <label className="tpo-field tpo-field--full">
                <span className="tpo-field__label">{chartText('photoLib.categoryApply', fixedUi("适用类目"))}</span>
                <input
                  className="tpo-input"
                  value={categoryPathLabel || categoryId || '—'}
                  disabled
                  readOnly
                />
              </label>
            </div>
          </div>
          <div className="tpo-batch-modal__foot">
            <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" disabled={saving} onClick={onClose}>
              {chartText('shop.cancel', '取消')}
            </button>
            <button
              type="submit"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={saving || (!officialMode && !shopId)}
            >
              {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('shop.save', fixedUi("保存"))}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
